import { createSelector, Selector } from 'reselect';

import { ApiTransactionAPIModelStatusEnum as TransactionAPIModelStatusEnum } from '@/generated/api/fps/core';
import { MessageId, PhoneType, TransactionId, Value } from '@/model/common';
import { makeSelectDefaultBank } from '@/model/dictionary/selectors';
import { RootState } from '@/model/types';
import { Timing, TransferFormDataCache, WithdrawalForm, WithdrawalStep } from '@/model/withdrawal/types';

export const makeSelectInitialized = (): Selector<RootState, boolean> =>
  createSelector(
    (state: RootState) => state.withdrawal.initialized,
    (state) => state,
  );

export const makeSelectFormData = (): Selector<RootState, WithdrawalForm | undefined> =>
  createSelector(
    (state: RootState) => state.withdrawal.formData,
    (state) => state,
  );

export const makeSelectPhone = (): Selector<RootState, Value<PhoneType> | undefined> =>
  createSelector(
    (state: RootState) => state.withdrawal.phone,
    (state) => state,
  );

export const makeSelectMessageId = (): Selector<RootState, MessageId | undefined> =>
  createSelector(
    (state: RootState) => state.withdrawal.formData?.id,
    (state) => state,
  );

export const makeSelectTiming = (): Selector<RootState, Timing | undefined> =>
  createSelector(
    (state: RootState) => state.withdrawal.timing,
    (state) => state,
  );

export const makeSelectTransferCached = (): Selector<RootState, TransferFormDataCache | undefined> =>
  createSelector(
    (state: RootState) => state.withdrawal.cache,
    (state) => state,
  );

export const makeSelectStep = (): Selector<
  RootState,
  { status?: TransactionAPIModelStatusEnum; sbpId?: string; transactionId?: TransactionId; step?: WithdrawalStep }
> =>
  createSelector(
    (state: RootState) => state.withdrawal.status,
    (state: RootState) => state.withdrawal.sbpId,
    (state: RootState) => state.withdrawal.transactionId,
    (state: RootState) => state.withdrawal.step,
    (status, sbpId, transactionId, step) => ({
      status,
      sbpId,
      transactionId,
      step,
    }),
  );

export interface ReceiverBank {
  id: string;
  name: string;
  icon?: string;
  priority: boolean;
}

export const makeSelectReceiverBanks = (): Selector<RootState, ReceiverBank[]> =>
  createSelector(
    (state: RootState) => state.withdrawal.formData?.receiverBanks,
    (state: RootState) => state.dictionary.banks,
    makeSelectDefaultBank(),
    (state: RootState) => state.withdrawal.formData?.receiver?.priorityBank,
    (receiverBanks, banks, defaultBank, priority) =>
      (receiverBanks || []).map(({ id, name }) => {
        const bankInfo = banks.find((b) => b.id === id || b.aliases.includes(id));
        return {
          id,
          name,
          icon: bankInfo?.icon,
          priority: priority === id,
        };
      }),
  );

const receiverBanksSelector = makeSelectReceiverBanks();
export const makeSelectReceiverBank = (id: string): Selector<RootState, ReceiverBank | undefined> =>
  createSelector(receiverBanksSelector, (state) => state.find(({ id: saved }) => saved === id));

export default {
  makeSelectInitialized,
  makeSelectReceiverBank,
  makeSelectReceiverBanks,
  makeSelectFormData,
  makeSelectStep,
};
